import type {ReactNode} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CollapseIcon from '@mui/icons-material/CloseFullscreenRounded';
import ExpandIcon from '@mui/icons-material/OpenInFullRounded';
import type {Call} from '../../api/calls';
import {callInitials, callTitle} from './callUtils';
import css from './Calls.module.css';

export function CallAvatar({name, small}: {name: string; small?: boolean}) {
	return (
		<span className={`${css.callAvatar} ${small ? css.callAvatarSmall : ''}`} aria-hidden>
			{callInitials(name) || '•'}
		</span>
	);
}

/**
 * Шапка окна звонка. Имя в ней стоит, только когда середину занимает видео или окно свёрнуто:
 * без видео собеседник крупно посреди окна, и второе имя в шапке было бы повтором.
 */
export function CallBar({
	call,
	compact,
	showName,
	status,
	recording,
	onToggleCompact,
}: {
	call: Call;
	compact: boolean;
	showName: boolean;
	status?: string | null;
	recording?: boolean;
	onToggleCompact: () => void;
}) {
	const identity = (
		<span className={css.barText}>
			<strong>{callTitle(call)}</strong>
			{status && <span>{status}</span>}
		</span>
	);
	return (
		<header className={css.callBar}>
			{compact ? (
				// Свёрнутое окно разворачивается нажатием на собеседника, как полоса звонка
				// в мессенджере.
				<button type="button" className={css.barIdentity} onClick={onToggleCompact}>
					<CallAvatar name={callTitle(call)} small />
					{identity}
				</button>
			) : showName ? (
				<div className={css.barIdentity}>{identity}</div>
			) : (
				<span className={css.barSpacer} />
			)}
			{recording && (
				<span className={css.recordingPill} title="Запись и распознавание включены">
					<span className={css.recordingDot} aria-hidden />
					{compact ? (
						<span className={css.srOnly}>Запись и распознавание включены</span>
					) : (
						'Запись и распознавание включены'
					)}
				</span>
			)}
			<button
				type="button"
				className={css.barButton}
				aria-label={compact ? 'Развернуть звонок' : 'Свернуть звонок'}
				title={compact ? 'Развернуть' : 'Свернуть'}
				onClick={onToggleCompact}
			>
				{compact ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
			</button>
		</header>
	);
}

/** Собеседник посреди окна, пока его видео нет: инициалы, имя, кто он и что со звонком. */
export function CallIdentity({
	call,
	details,
	name = callTitle(call),
	headline,
	status,
	children,
}: {
	call: Call;
	/** Роль и компания собеседника — `callDetails`: сторону смотрящего знает оболочка звонка. */
	details: string[];
	name?: string;
	headline?: string | null;
	status?: string | null;
	children?: ReactNode;
}) {
	return (
		<div className={css.identityBlock}>
			<CallAvatar name={name} />
			<strong className={css.identityName}>{name}</strong>
			{details.map((line) => (
				<span key={line} className={css.identityLine}>
					{line}
				</span>
			))}
			{headline && <span className={css.identityHeadline}>{headline}</span>}
			{status && (
				<span className={css.identityStatus} role="status">
					{status}
				</span>
			)}
			{children}
		</div>
	);
}

/**
 * Круглая кнопка звонка. Подпись — существительное («Микрофон»), а состояние видно по самой
 * кнопке: белая — устройство работает, полупрозрачная с перечёркнутым значком — выключено.
 * Подсказка при наведении называет действие.
 */
export function CallControl({
	icon,
	label,
	hint,
	onClick,
	on,
	disabled,
	loading,
	tone,
	compact,
	accessory,
}: {
	icon: ReactNode;
	label: string;
	hint?: string;
	onClick: () => void;
	/** Для переключателей: устройство работает. У кнопок-действий не задаётся. */
	on?: boolean;
	disabled?: boolean;
	loading?: boolean;
	tone?: 'danger' | 'accept';
	compact?: boolean;
	/** Малая кнопка у края круглой — выбор устройства. */
	accessory?: ReactNode;
}) {
	const classes = [
		css.controlButton,
		on ? css.controlOn : '',
		tone === 'danger' ? css.controlDanger : '',
		tone === 'accept' ? css.controlAccept : '',
	]
		.filter(Boolean)
		.join(' ');
	return (
		<div className={css.control}>
			<span className={css.controlFrame}>
				<button
					type="button"
					className={classes}
					aria-label={label}
					aria-pressed={on}
					title={hint ?? label}
					disabled={disabled || loading}
					onClick={onClick}
				>
					{loading ? <CircularProgress size={22} color="inherit" /> : icon}
				</button>
				{accessory}
			</span>
			{!compact && (
				<span className={css.controlLabel} aria-hidden>
					{label}
				</span>
			)}
		</div>
	);
}

/** Сообщение поверх окна: ошибка — тревожным цветом, остальное — нейтральной плашкой. */
export function CallNotice({
	children,
	alarming,
	action,
}: {
	children: ReactNode;
	alarming?: boolean;
	action?: {label: string; onClick: () => void};
}) {
	return (
		<div
			className={`${css.callNotice} ${alarming ? css.callNoticeAlarming : ''}`}
			role={alarming ? 'alert' : 'status'}
		>
			<span>{children}</span>
			{action && (
				<button type="button" className={css.callNoticeAction} onClick={action.onClick}>
					{action.label}
				</button>
			)}
		</div>
	);
}
